(function (globalScope) {
  const GIFT_FLAG_PROPERTY = '_bundle_gift';
  const OFFER_ID_PROPERTY = '_bundle_offer_id';
  const GIFT_FLAG_VALUE = 'true';
  const OFFERS_SELECTOR = '[data-bundle-offers]';
  const TRANSLATIONS_SELECTOR = '[data-bundle-offer-translations]';
  const BAR_SELECTOR = '[data-bundle-offer-bar]';
  const CART_REFRESH_EVENT = 'theme:cart:refresh';
  const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update'];
  const CART_RESPONSE_PATHS = ['/cart/change', '/cart/update'];
  const JSON_CONTENT_TYPE = 'application/json';
  const DEFAULT_TRANSLATIONS = {
    fallbackTitle: 'Bracelet FKM offert',
    giftAdded: 'Cadeau ajouté au panier',
    giftUnlocked: 'Cadeau débloqué',
    giftDefault: 'Offert avec votre montre',
    viewProduct: 'Voir le produit',
    close: 'Fermer',
  };

  let validationPromise = null;
  let validationRequested = false;
  let pendingCartPromise = null;
  let isApplyingCartActions = false;
  let activeCart = null;
  let activeOffers = [];
  let activeTranslations = { ...DEFAULT_TRANSLATIONS };
  let modalElement = null;
  let modalOffer = null;
  let modalCloseTimer = null;

  function normalizePositiveInteger(value) {
    const normalizedValue = Number.parseInt(value, 10);

    if (!Number.isFinite(normalizedValue) || normalizedValue < 1) {
      return 0;
    }

    return normalizedValue;
  }

  function normalizeString(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function normalizeStringArray(value) {
    if (!Array.isArray(value)) {
      return [];
    }

    return Array.from(new Set(value.map(normalizeString).filter(Boolean)));
  }

  function normalizeMedia(media) {
    if (!Array.isArray(media)) {
      return [];
    }

    return media
      .map((item) => ({
        type: normalizeString(item?.type),
        alt: normalizeString(item?.alt),
        preview: normalizeString(item?.preview),
        image: normalizeString(item?.image),
        source: normalizeString(item?.source),
        mimeType: normalizeString(item?.mimeType) || 'video/mp4',
        host: normalizeString(item?.host),
        externalId: normalizeString(item?.externalId),
      }))
      .filter((item) => item.type === 'image' || item.type === 'video' || item.type === 'external_video');
  }

  function normalizeOffers(offers) {
    if (!Array.isArray(offers)) {
      return [];
    }

    return offers
      .map((offer) => {
        const reward = offer?.reward || {};
        const triggerProduct = offer?.triggerProduct || {};
        const rewardVariantId = normalizePositiveInteger(reward.variantId);
        const triggerProductId = normalizePositiveInteger(triggerProduct.id);
        const media = normalizeMedia(reward.media);

        if (media.length === 0 && normalizeString(reward.image)) {
          media.push({
            type: 'image',
            alt: normalizeString(reward.title),
            preview: normalizeString(reward.image),
            image: normalizeString(reward.image),
          });
        }

        return {
          id: normalizeString(offer?.id) || `bundle-offer-${rewardVariantId}`,
          title: normalizeString(offer?.title),
          priority: normalizePositiveInteger(offer?.priority) || 10,
          minQuantity: normalizePositiveInteger(offer?.minQuantity) || 1,
          displayLocations: normalizeStringArray(offer?.displayLocations),
          triggerProduct: {
            id: triggerProductId,
            title: normalizeString(triggerProduct.title),
            handle: normalizeString(triggerProduct.handle),
            url: normalizeString(triggerProduct.url),
            image: normalizeString(triggerProduct.image),
          },
          reward: {
            productId: normalizePositiveInteger(reward.productId),
            variantId: rewardVariantId,
            quantity: normalizePositiveInteger(reward.quantity) || 1,
            title: normalizeString(reward.title),
            variantTitle: normalizeString(reward.variantTitle),
            url: normalizeString(reward.url),
            image: normalizeString(reward.image),
            media,
          },
        };
      })
      .filter((offer) => offer.id && offer.triggerProduct.id > 0 && offer.reward.variantId > 0);
  }

  function normalizeTranslations(translations) {
    if (!translations || typeof translations !== 'object') {
      return { ...DEFAULT_TRANSLATIONS };
    }

    return Object.keys(DEFAULT_TRANSLATIONS).reduce((normalizedTranslations, key) => {
      const value = normalizeString(translations[key]);
      normalizedTranslations[key] = value || DEFAULT_TRANSLATIONS[key];
      return normalizedTranslations;
    }, {});
  }

  function readTranslations(root = document) {
    const script = root?.querySelector?.(TRANSLATIONS_SELECTOR);

    if (!script) {
      return { ...DEFAULT_TRANSLATIONS };
    }

    try {
      return normalizeTranslations(JSON.parse(script.textContent || '{}'));
    } catch (error) {
      console.error('Bundle Offer Translations Error:', error);
      return { ...DEFAULT_TRANSLATIONS };
    }
  }

  function getTranslation(key) {
    return activeTranslations[key] || DEFAULT_TRANSLATIONS[key] || '';
  }

  function readOffers(root = document) {
    const script = root?.querySelector?.(OFFERS_SELECTOR);

    if (!script) {
      return [];
    }

    try {
      return normalizeOffers(JSON.parse(script.textContent || '[]'));
    } catch (error) {
      console.error('Bundle Offer Rules Error:', error);
      return [];
    }
  }

  function getLineProperties(item) {
    return item?.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function isBundleGiftLine(item) {
    return getLineProperties(item)[GIFT_FLAG_PROPERTY] === GIFT_FLAG_VALUE;
  }

  function getBundleOfferId(item) {
    const offerId = getLineProperties(item)[OFFER_ID_PROPERTY];

    return typeof offerId === 'string' ? offerId : '';
  }

  function buildCartLines(cart) {
    return (cart?.items || []).map((item) => ({
      key: item.key,
      productId: normalizePositiveInteger(item.product_id),
      variantId: normalizePositiveInteger(item.variant_id || item.id),
      quantity: normalizePositiveInteger(item.quantity),
      image: normalizeString(item.image),
      title: normalizeString(item.product_title || item.title),
      properties: getLineProperties(item),
      isBundleGift: isBundleGiftLine(item),
      bundleOfferId: getBundleOfferId(item),
    }));
  }

  function lineMatchesTrigger(line, offer) {
    return line && !line.isBundleGift && line.productId === offer.triggerProduct.id;
  }

  function lineMatchesRewardVariant(line, offer) {
    return line && line.variantId === offer.reward.variantId;
  }

  function isRewardInCart(lines, offer) {
    return lines.some(
      (line) => line.isBundleGift && line.bundleOfferId === offer.id && lineMatchesRewardVariant(line, offer)
    );
  }

  function computeOfferProgress(lines, offer) {
    const qualifiedQuantity = lines.reduce((total, line) => {
      if (!lineMatchesTrigger(line, offer)) {
        return total;
      }

      return total + line.quantity;
    }, 0);
    const remaining = Math.max(offer.minQuantity - qualifiedQuantity, 0);

    return {
      qualifiedQuantity,
      remaining,
      unlocked: remaining === 0,
    };
  }

  function buildGiftProperties(offer) {
    return {
      [GIFT_FLAG_PROPERTY]: GIFT_FLAG_VALUE,
      [OFFER_ID_PROPERTY]: offer.id,
    };
  }

  function computeBundleOfferActions({ cart, offers = activeOffers } = {}) {
    const lines = buildCartLines(cart);
    const updates = {};
    const additions = [];

    offers.forEach((offer) => {
      const progress = computeOfferProgress(lines, offer);
      const desiredQuantity = progress.unlocked ? offer.reward.quantity : 0;
      const giftLines = lines.filter((line) => line.isBundleGift && line.bundleOfferId === offer.id);
      const manualRewardLine = lines.some((line) => lineMatchesRewardVariant(line, offer) && !line.isBundleGift);

      if (desiredQuantity > 0 && giftLines.length === 0) {
        if (!manualRewardLine) {
          additions.push({
            id: offer.reward.variantId,
            quantity: desiredQuantity,
            properties: buildGiftProperties(offer),
          });
        }

        return;
      }

      giftLines.forEach((giftLine, index) => {
        const nextQuantity = index === 0 ? desiredQuantity : 0;

        if (giftLine.key && giftLine.quantity !== nextQuantity) {
          updates[giftLine.key] = nextQuantity;
        }
      });
    });

    return { additions, updates };
  }

  function hasActions(actions) {
    return Boolean(actions?.additions?.length) || Object.keys(actions?.updates || {}).length > 0;
  }

  function getRootRoute() {
    return globalScope.Shopify?.routes?.root || globalScope.theme?.routes?.root || '/';
  }

  async function fetchCart() {
    const response = await fetch(`${getRootRoute()}cart.js`, { credentials: 'same-origin' });
    return response.json();
  }

  async function applyCartUpdates(updates) {
    await fetch(`${getRootRoute()}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': JSON_CONTENT_TYPE,
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
  }

  async function applyCartAdditions(additions) {
    await fetch(`${getRootRoute()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': JSON_CONTENT_TYPE,
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: additions }),
    });
  }

  async function applyCartActions(actions) {
    if (!hasActions(actions)) {
      return false;
    }

    isApplyingCartActions = true;

    try {
      if (Object.keys(actions.updates).length > 0) {
        await applyCartUpdates(actions.updates);
      }

      if (actions.additions.length > 0) {
        await applyCartAdditions(actions.additions);
      }
    } finally {
      isApplyingCartActions = false;
    }

    document.dispatchEvent(new CustomEvent(CART_REFRESH_EVENT, { bubbles: true }));
    return true;
  }

  function createSlotImage(src, alt) {
    if (!src) {
      return null;
    }

    const image = document.createElement('img');
    image.src = src;
    image.alt = alt || '';
    image.loading = 'lazy';
    image.decoding = 'async';

    return image;
  }

  function createSvgElement(tag, attributes = {}) {
    const node = document.createElementNS('http://www.w3.org/2000/svg', tag);

    Object.entries(attributes).forEach(([key, value]) => {
      node.setAttribute(key, value);
    });

    return node;
  }

  function createGiftIcon() {
    const icon = createSvgElement('svg', {
      'aria-hidden': 'true',
      focusable: 'false',
      role: 'presentation',
      class: 'bundle-bar__state-icon',
      viewBox: '-3 -3 30 30',
    });
    const path = createSvgElement('path', {
      d: 'M20 12v9.4a.6.6 0 0 1-.6.6H4.6a.6.6 0 0 1-.6-.6V12m17.4-5H2.6a.6.6 0 0 0-.6.6v3.8a.6.6 0 0 0 .6.6h18.8a.6.6 0 0 0 .6-.6V7.6a.6.6 0 0 0-.6-.6zM12 22V7m0 0H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.25',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });

    icon.append(path);

    return icon;
  }

  function createSuccessIcon() {
    const icon = createSvgElement('svg', {
      'aria-hidden': 'true',
      focusable: 'false',
      role: 'presentation',
      class: 'bundle-bar__success-icon',
      viewBox: '0 0 18 18',
    });
    const circle = createSvgElement('circle', {
      cx: '9',
      cy: '9',
      r: '8',
      fill: 'currentColor',
    });
    const path = createSvgElement('path', {
      d: 'M5.4 9.2l2.2 2.2 5-5.1',
      fill: 'none',
      stroke: '#fff',
      'stroke-width': '1.55',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });

    icon.append(circle, path);

    return icon;
  }

  function renderGiftIcon(element) {
    if (element) {
      element.replaceChildren(createGiftIcon());
    }
  }

  function createArrowIcon() {
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('class', 'bundle-bar__reward-link-icon');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '<path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>';

    return icon;
  }

  function setElementText(element, text) {
    if (element) {
      element.textContent = text || '';
    }
  }

  function renderOfferSubtitle(element, text, rewardInCart) {
    if (!element) {
      return;
    }

    const label = document.createElement('span');
    label.textContent = text || '';
    element.replaceChildren();

    if (rewardInCart) {
      element.append(createSuccessIcon());
    }

    element.append(label);
  }

  function getBestOfferForBar(bar, offers = activeOffers) {
    const location = normalizeString(bar.getAttribute('data-bundle-offer-location'));
    const productId = normalizePositiveInteger(bar.getAttribute('data-bundle-offer-product-id'));

    return offers
      .filter((offer) => {
        const locations = offer.displayLocations.length ? offer.displayLocations : ['pdp'];

        if (!locations.includes(location)) {
          return false;
        }

        return location !== 'pdp' || productId === 0 || productId === offer.triggerProduct.id;
      })
      .sort((firstOffer, secondOffer) => secondOffer.priority - firstOffer.priority)[0];
  }

  function createMediaItem(image, title, className = '') {
    const item = document.createElement('span');
    item.className = `bundle-bar__media-item${className ? ` ${className}` : ''}`;

    const mediaImage = createSlotImage(image, title);

    if (mediaImage) {
      item.append(mediaImage);
    }

    return item;
  }

  function renderOfferMedia(container, offer) {
    if (!container) {
      return;
    }

    container.replaceChildren(
      createMediaItem(offer.reward.image, offer.reward.title, 'bundle-bar__media-item--reward')
    );
  }

  function getOfferHeadline(offer) {
    return offer.title || getTranslation('fallbackTitle');
  }

  function getOfferSubtitle(progress, rewardInCart) {
    if (rewardInCart) {
      return getTranslation('giftAdded');
    }

    if (progress.unlocked) {
      return getTranslation('giftUnlocked');
    }

    return getTranslation('giftDefault');
  }

  function renderRewardAction(container, offer) {
    if (!container) {
      return;
    }

    const hasRewardPreview = offer.reward.media.length > 0 || Boolean(offer.reward.image);
    container.replaceChildren();

    if (!hasRewardPreview) {
      return;
    }

    const rewardLink = document.createElement('button');
    rewardLink.className = 'bundle-bar__reward-link';
    rewardLink.type = 'button';
    rewardLink.addEventListener('click', () => openModal(offer));

    const rewardLinkText = document.createElement('span');
    rewardLinkText.className = 'bundle-bar__reward-link-text';
    rewardLinkText.textContent = getTranslation('viewProduct');
    rewardLink.append(rewardLinkText, createArrowIcon());
    container.append(rewardLink);
  }

  function renderOfferBar(bar, cart, offer) {
    const lines = buildCartLines(cart);
    const progress = computeOfferProgress(lines, offer);
    const rewardInCart = isRewardInCart(lines, offer);
    const mediaElement = bar.querySelector('[data-bundle-media]');
    const iconElement = bar.querySelector('[data-bundle-icon]');
    const headlineElement = bar.querySelector('[data-bundle-headline]');
    const subtitleElement = bar.querySelector('[data-bundle-subtitle]');
    const rewardAction = bar.querySelector('[data-bundle-reward-action]');

    renderOfferMedia(mediaElement, offer);
    renderGiftIcon(iconElement);
    setElementText(headlineElement, getOfferHeadline(offer));
    renderOfferSubtitle(subtitleElement, getOfferSubtitle(progress, rewardInCart), rewardInCart);

    renderRewardAction(rewardAction, offer);

    bar.hidden = false;
    bar.dataset.bundleOfferId = offer.id;
  }

  function renderBundleOffers(cart = activeCart) {
    if (!cart || activeOffers.length === 0) {
      document.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
        bar.hidden = true;
      });
      return;
    }

    document.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
      const offer = getBestOfferForBar(bar);

      if (!offer) {
        bar.hidden = true;
        return;
      }

      renderOfferBar(bar, cart, offer);
    });
  }

  async function validateBundleOfferCart(cart = null) {
    try {
      if (activeOffers.length === 0) {
        return;
      }

      const resolvedCart = cart || (await fetchCart());
      const actions = computeBundleOfferActions({
        cart: resolvedCart,
        offers: activeOffers,
      });
      const changedCart = await applyCartActions(actions);

      if (changedCart) {
        const refreshedCart = await fetchCart();
        activeCart = refreshedCart;
        renderBundleOffers(refreshedCart);
      } else {
        activeCart = resolvedCart;
        renderBundleOffers(resolvedCart);
      }
    } catch (error) {
      console.error('Bundle Offer Cart Validation Error:', error);
    }
  }

  function requestCartValidation(cartPromise = null) {
    validationRequested = true;

    if (cartPromise) {
      pendingCartPromise = cartPromise;
    }

    if (validationPromise) {
      return validationPromise;
    }

    validationPromise = (async () => {
      while (validationRequested) {
        validationRequested = false;
        const nextCartPromise = pendingCartPromise;
        pendingCartPromise = null;
        const nextCart = nextCartPromise ? await nextCartPromise : null;
        await validateBundleOfferCart(nextCart);
      }
    })().finally(() => {
      validationPromise = null;
    });

    return validationPromise;
  }

  function shouldValidateCart(responseUrl) {
    return CART_MUTATION_PATHS.some((path) => responseUrl.includes(path));
  }

  function getRequestUrl(input) {
    if (typeof input === 'string') {
      return input;
    }

    if (input instanceof URL) {
      return input.href;
    }

    if (input && typeof input.url === 'string') {
      return input.url;
    }

    return '';
  }

  function canUseResponseCart(responseUrl) {
    return CART_RESPONSE_PATHS.some((path) => responseUrl.includes(path));
  }

  async function getCartFromResponse(response, responseUrl) {
    if (!response || typeof response.clone !== 'function' || !canUseResponseCart(responseUrl)) {
      return null;
    }

    try {
      const responseCart = await response.clone().json();

      if (Array.isArray(responseCart?.items)) {
        return responseCart;
      }
    } catch (error) {
      console.warn('Bundle Offer Cart Snapshot Error:', error);
    }

    return null;
  }

  function patchWindowFetch() {
    if (globalScope.__belvansBundleOfferFetchPatched) {
      return;
    }

    const originalFetch = globalScope.fetch;
    globalScope.__belvansBundleOfferFetchPatched = true;

    globalScope.fetch = async function patchedFetch(...args) {
      const requestUrl = getRequestUrl(args[0]);
      const response = await originalFetch.apply(this, args);
      const responseUrl = response?.url || requestUrl;

      if (!isApplyingCartActions && shouldValidateCart(responseUrl || '')) {
        requestCartValidation(getCartFromResponse(response, responseUrl));
      }

      return response;
    };

    if (globalScope.window && globalScope.window.fetch !== globalScope.fetch) {
      globalScope.window.fetch = (...args) => globalScope.fetch(...args);
    }
  }

  function getModalMedia(offer) {
    if (offer.reward.media.length > 0) {
      return offer.reward.media;
    }

    if (offer.reward.image) {
      return [
        {
          type: 'image',
          alt: offer.reward.title,
          preview: offer.reward.image,
          image: offer.reward.image,
        },
      ];
    }

    return [];
  }

  function createModal() {
    const modal = document.createElement('div');
    modal.className = 'bundle-offer-modal';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = `
      <div class="bundle-offer-modal__dialog" role="document">
        <div class="bundle-offer-modal__header">
          <h2 class="bundle-offer-modal__title" data-bundle-offer-modal-title></h2>
          <button class="bundle-offer-modal__close" type="button" data-bundle-offer-modal-close></button>
        </div>
        <div class="bundle-offer-modal__body">
          <div class="bundle-offer-modal__stage" data-bundle-offer-modal-stage></div>
          <div class="bundle-offer-modal__rail" data-bundle-offer-modal-rail></div>
        </div>
      </div>
    `;

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeModal();
      }
    });
    const closeButton = modal.querySelector('[data-bundle-offer-modal-close]');
    closeButton?.setAttribute('aria-label', getTranslation('close'));
    closeButton?.addEventListener('click', closeModal);
    document.body.append(modal);

    return modal;
  }

  function renderMediaItem(stage, item, offer) {
    stage.replaceChildren();

    if (item.type === 'video' && item.source) {
      const video = document.createElement('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.poster = item.preview || '';

      const source = document.createElement('source');
      source.src = item.source;
      source.type = item.mimeType || 'video/mp4';
      video.append(source);
      stage.append(video);
      return;
    }

    if (item.type === 'external_video' && item.host === 'youtube' && item.externalId) {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(item.externalId)}`;
      iframe.title = item.alt || offer.reward.title;
      iframe.loading = 'lazy';
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      stage.append(iframe);
      return;
    }

    const image = document.createElement('img');
    image.src = item.image || item.preview || offer.reward.image;
    image.alt = item.alt || offer.reward.title;
    image.loading = 'eager';
    image.decoding = 'async';
    stage.append(image);
  }

  function setActiveModalMedia(index) {
    if (!modalElement || !modalOffer) {
      return;
    }

    const media = getModalMedia(modalOffer);
    const item = media[index] || media[0];
    const stage = modalElement.querySelector('[data-bundle-offer-modal-stage]');

    if (!stage || !item) {
      return;
    }

    renderMediaItem(stage, item, modalOffer);

    modalElement.querySelectorAll('[data-bundle-offer-modal-thumb]').forEach((button, buttonIndex) => {
      button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false');
    });
  }

  function openModal(offer) {
    modalOffer = offer;
    modalElement = modalElement || createModal();

    const title = modalElement.querySelector('[data-bundle-offer-modal-title]');
    const rail = modalElement.querySelector('[data-bundle-offer-modal-rail]');
    const media = getModalMedia(offer);

    setElementText(title, offer.reward.title);

    if (rail) {
      rail.replaceChildren();
      media.forEach((item, index) => {
        const button = document.createElement('button');
        button.className = 'bundle-offer-modal__thumb';
        button.type = 'button';
        button.setAttribute('data-bundle-offer-modal-thumb', '');
        button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        button.addEventListener('click', () => setActiveModalMedia(index));

        const image = document.createElement('img');
        image.src = item.preview || item.image || offer.reward.image;
        image.alt = item.alt || offer.reward.title;
        image.loading = 'lazy';
        image.decoding = 'async';
        button.append(image);
        rail.append(button);
      });
    }

    setActiveModalMedia(0);

    if (modalCloseTimer) {
      clearTimeout(modalCloseTimer);
      modalCloseTimer = null;
    }

    modalElement.hidden = false;
    document.documentElement.classList.add('bundle-offer-modal-open');
    requestAnimationFrame(() => {
      modalElement?.classList.add('is-open');
    });
  }

  function closeModal() {
    if (!modalElement || modalElement.hidden) {
      return;
    }

    modalElement.classList.remove('is-open');
    document.documentElement.classList.remove('bundle-offer-modal-open');

    if (modalCloseTimer) {
      clearTimeout(modalCloseTimer);
    }

    modalCloseTimer = setTimeout(() => {
      if (!modalElement?.classList.contains('is-open')) {
        modalElement.hidden = true;
        modalElement.querySelector('[data-bundle-offer-modal-stage]')?.replaceChildren();
      }

      modalCloseTimer = null;
    }, 360);
  }

  function bindModalEscape() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modalElement && !modalElement.hidden) {
        closeModal();
      }
    });
  }

  const api = {
    GIFT_FLAG_PROPERTY,
    OFFER_ID_PROPERTY,
    GIFT_FLAG_VALUE,
    buildCartLines,
    computeBundleOfferActions,
    computeOfferProgress,
    getCartFromResponse,
    getRequestUrl,
    isBundleGiftLine,
    normalizeOffers,
    readTranslations,
    readOffers,
    requestCartValidation,
    shouldValidateCart,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    globalScope.BelvansBundleOffer = api;

    document.addEventListener('DOMContentLoaded', () => {
      activeTranslations = readTranslations();
      activeOffers = readOffers();

      if (activeOffers.length === 0) {
        return;
      }

      bindModalEscape();
      requestCartValidation();
    });
    document.addEventListener(CART_REFRESH_EVENT, (event) => requestCartValidation(Promise.resolve(event?.detail?.cart || null)));
    patchWindowFetch();
  }
})(typeof window !== 'undefined' ? window : globalThis);
